import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useWalletsV2 } from './useWalletsV2';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { notify } from 'src/utils/notifications';

/**
 * Hook to handle fee payer errors when using embedded wallet + Velocity's Fee Payer to sign transactions.
 */
export const useAutoSignVelocityActionWithFallback = <
	T,
	Args extends unknown[],
>(
	action: (
		wallet: UiEnhancedIWalletV2,
		isAutoSigned: boolean,
		toastId: string,
		...args: Args
	) => Promise<T>,
	disableGasSponsor = false
): ((toastId: string, ...args: Args) => Promise<T>) => {
	const {
		preferredTradingSignerWallet,
		authorityWallet,
		authorityWalletNoFeePayer,
		isEmbeddedWalletThePreferredTradingSigner,
	} = useWalletsV2();

	/**
	 * Handles fee payer errors (if using embedded wallet + Velocity's Fee Payer to sign transactions),
	 * by falling back to using the authority wallet to manually sign the transaction.
	 *
	 * We use the same toastId for both the main execution and fallback execution, so that the existing
	 * toast will be updated during the fallback execution.
	 */
	const autoSignVelocityActionWithFallback = useCallback(
		async (toastId: string, ...args: Args): Promise<T> => {
			invariant(
				preferredTradingSignerWallet,
				'preferredTradingSignerWallet is required'
			);
			invariant(authorityWallet, 'authorityWallet is required');

			if (disableGasSponsor) {
				invariant(
					authorityWalletNoFeePayer,
					'authorityWalletNoFeePayer is required'
				);
				return action(authorityWalletNoFeePayer, false, toastId, ...args);
			}

			// user is manually signing the transaction/message, error should be handled by the action
			if (!isEmbeddedWalletThePreferredTradingSigner) {
				return action(
					preferredTradingSignerWallet,
					isEmbeddedWalletThePreferredTradingSigner,
					toastId,
					...args
				);
			}

			try {
				return await action(
					preferredTradingSignerWallet,
					isEmbeddedWalletThePreferredTradingSigner,
					toastId,
					...args
				);
			} catch (e) {
				console.error('Error signing transaction/message', e);

				notify({
					type: 'info',
					message:
						'Failed to auto-sign transaction. Please sign transaction manually.',
				});

				if (e instanceof FeePayerError) {
					// Use wallet that skips fee payer so user signs and pays for tx themselves
					const fallbackWallet = authorityWalletNoFeePayer ?? authorityWallet;
					return action(fallbackWallet, false, toastId, ...args);
				}

				throw e;
			}
		},
		[
			action,
			preferredTradingSignerWallet,
			authorityWallet,
			authorityWalletNoFeePayer,
			isEmbeddedWalletThePreferredTradingSigner,
			disableGasSponsor,
		]
	);

	return autoSignVelocityActionWithFallback;
};
